import {
  getAdviceChangedByCustomer,
  getCorrectPayment,
  isAdviceProductOnCart,
  isChecked,
} from "Store/advice/utils";
import { useEffect, useState } from "react";
import {
  AdviceData,
  AdvisorValues,
} from "../../ProductDetails/FormElements/interfaces";
import {
  AdvicePriceFormValues,
  OneTimePaymentAdvisorChoice,
} from "../AdviceCards/interfaces";
import {
  useAdvicePriceCalculationRequests,
  useAdvicePriceCalculationResults,
  useAdvisorValues,
} from "./stateSelectors";

import { formatNumber } from "Formatting/index";
import { ADVICE_NAMES } from "Store/advice/interfaces";
import { getCorrectAdviceValues } from "Store/advice/productPriceRequestMappers";
import { useCartSelector } from "Store/cart";
import { Cart } from "Store/cart/cartInterfaces";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { CriticalIllnessProduct } from "Store/interfaces/products/checkout/lifeProducts/CriticalIllnessProduct";
import { DeathProduct } from "Store/interfaces/products/checkout/lifeProducts/DeathProduct";
import { DisabilityProduct } from "Store/interfaces/products/checkout/lifeProducts/DisabilityProduct";
import { booleanToFormBoolean } from "Utils/formUtils";
import { isDefined } from "Utils/functions";
import { useAdvicePriceFormValues } from "./useAdvicePriceFormValues";
import { useAdviceProductsAdviced } from "./useAdviceProductsAdviced";

export const useInitialFormValues = (
  isAdvisor: boolean,
): AdvicePriceFormValues | undefined => {
  const {
    disabilityProductAdviced,
    deathProductAdviced,
    criticalIllnessProductAdviced,
  } = useAdviceProductsAdviced();
  const advisorValues = useAdvisorValues();
  const advicePrice = useAdvicePriceFormValues();
  const advicePriceCalculationRequests = useAdvicePriceCalculationRequests();
  const advicePriceCalculationResults = useAdvicePriceCalculationResults();
  const { current: cart } = useCartSelector();

  const getCorrectFormValues = (
    changes: AdviceData[],
  ): AdvicePriceFormValues => ({
    isAdvisor,
    disabilityCheckedToPurchase: getProductCheckedValue(
      changes,
      INSURANCE_NAME_VALUE.DISABILITY_FLEX,
      isChecked(disabilityProductAdviced?.insuranceAdvice),
    ),
    disabilityInsuredSumValue: getProductSliderValue(
      changes,
      INSURANCE_NAME_VALUE.DISABILITY_FLEX,
      cart,
      isDefined(disabilityProductAdviced)
        ? getCorrectAdviceValues(
            getCorrectPayment(disabilityProductAdviced.insuranceAdvice),
            isAdvisor,
          ).adviceValue
        : 0,
      isAdvisor,
    ),
    disabilityPaymentAfter3Months: getProductPaymentAfter3Months(changes),
    ...getCorrectDisabilityOneTimePaymentFormValues(changes, isAdvisor),
    deathCheckedToPurchase: getProductCheckedValue(
      changes,
      INSURANCE_NAME_VALUE.DEATH,
      isChecked(deathProductAdviced?.insuranceAdvice),
    ),
    deathInsuredSumValue: getProductSliderValue(
      changes,
      INSURANCE_NAME_VALUE.DEATH,
      cart,
      isDefined(deathProductAdviced)
        ? getCorrectAdviceValues(
            getCorrectPayment(deathProductAdviced.insuranceAdvice),
            isAdvisor,
          ).adviceValue
        : 0,
      isAdvisor,
    ),
    criticalIllnessCheckedToPurchase: getProductCheckedValue(
      changes,
      INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
      isChecked(criticalIllnessProductAdviced?.insuranceAdvice),
    ),
    criticalIllnessInsuredSumValue: getProductSliderValue(
      changes,
      INSURANCE_NAME_VALUE.CRITICAL_ILLNESS,
      cart,
      isDefined(criticalIllnessProductAdviced)
        ? getCorrectAdviceValues(
            getCorrectPayment(criticalIllnessProductAdviced.insuranceAdvice),
            isAdvisor,
          ).adviceValue
        : 0,
      isAdvisor,
    ),
    childProductCheckedToPurchase: false,
    adviceComment:
      advicePrice?.adviceComment ?? getAdviceComment(advisorValues),
    adviceDiffConsent: false,
  });

  const [initialFormValues, setInitialFormValues] = useState<
    AdvicePriceFormValues | undefined
  >(undefined);

  useEffect(() => {
    if (
      !isDefined(initialFormValues) &&
      (advicePriceCalculationResults?.length ?? 0) > 0
    ) {
      let currentFormValues = getCorrectFormValues([]);
      const changes = getAdviceChangedByCustomer(
        advicePriceCalculationRequests.changedByCustomer,
      );
      if (changes && changes.length > 0) {
        currentFormValues = getCorrectFormValues(changes);
      }
      setInitialFormValues(currentFormValues);
    }
  }, [advicePriceCalculationResults]);

  return initialFormValues;
};

const getInsurance = (
  changes: AdviceData[],
  insuranceName: INSURANCE_NAME_VALUE,
): AdviceData | undefined =>
  changes.find((insurance) => insurance.insuranceName === insuranceName);

export const getProductCheckedValue = (
  changes: AdviceData[],
  insuranceName: INSURANCE_NAME_VALUE,
  checked: boolean,
): boolean => {
  const value = getInsurance(changes, insuranceName)?.productCheckedToPurchase;
  return value ?? checked;
};

export const getProductOneTimePaymentCheckedValue = (
  changes: AdviceData[],
): boolean => {
  const value = getInsurance(changes, INSURANCE_NAME_VALUE.DISABILITY_FLEX)
    ?.additionalData?.disabilityOneTimePaymentChecked;
  return value ?? false;
};

const getProductOneTimePaymentValue = (
  changes: AdviceData[],
): number | undefined => {
  return getInsurance(changes, INSURANCE_NAME_VALUE.DISABILITY_FLEX)
    ?.additionalData?.disabilityOneTimePaymentValue;
};

const getProductPaymentAfter3Months = (
  changes: AdviceData[],
): BooleanFormType => {
  const value = getInsurance(changes, INSURANCE_NAME_VALUE.DISABILITY_FLEX)
    ?.additionalData?.disabilityPaymentAfter3Months;
  if (!isDefined(value)) {
    return BooleanFormType.FALSE;
  }
  return booleanToFormBoolean(value);
};

const getProductOneTimePaymentAdvisorChoice = (
  changes: AdviceData[],
): OneTimePaymentAdvisorChoice => {
  const value = getInsurance(changes, INSURANCE_NAME_VALUE.DISABILITY_FLEX)
    ?.additionalData?.disabilityOneTimePaymentChoice;
  if (!isDefined(value)) {
    return OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT;
  }
  return value;
};

const getProductOneTimePaymentAdvisorValue = (
  changes: AdviceData[],
): string => {
  const hasAdvisorChosenNoOneTimePayment =
    getProductOneTimePaymentAdvisorChoice(changes) ===
    OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT;
  const value = getInsurance(changes, INSURANCE_NAME_VALUE.DISABILITY_FLEX)
    ?.additionalData?.disabilityOneTimePaymentAdvisorValue;
  if (hasAdvisorChosenNoOneTimePayment || !isDefined(value)) {
    return "0";
  }
  return formatNumber(value.toString(), false);
};

const getAdviceComment = (advisorValues?: AdvisorValues): string => {
  return advisorValues?.adviceComment ?? "";
};

export const getProductSliderValue = (
  changes: AdviceData[],
  insuranceName: ADVICE_NAMES,
  cart: Cart | null,
  adviceValue: number,
  isAdvisor = false,
): number => {
  if (isAdvisor) {
    const insuredSum = insuredSumFromAdviceProduct(insuranceName, cart);
    if (isDefined(insuredSum)) {
      return insuredSum;
    }
  }

  const sliderValue = getInsurance(changes, insuranceName)?.sliderValue;
  return sliderValue ?? adviceValue;
};

export const insuredSumFromAdviceProduct = (
  insuranceName: INSURANCE_NAME_VALUE,
  cart: Cart | null,
): number | undefined => {
  if (cart) {
    const currentProduct = cart.products.find(
      (product) =>
        product.name === insuranceName && isAdviceProductOnCart(product, cart),
    );
    if (isDefined(currentProduct)) {
      const adviceProduct = currentProduct as
        | DisabilityProduct
        | DeathProduct
        | CriticalIllnessProduct;
      return adviceProduct.insuredSum;
    }
  }
};

type DisabilityOneTimePaymentFormValues =
  | "disabilityOneTimePayment"
  | "disabilityOneTimePaymentValue"
  | "disabilityOneTimePaymentAdvisorChoice"
  | "disabilityOneTimePaymentAdvisorValue";

const getCorrectDisabilityOneTimePaymentFormValues = (
  changes: AdviceData[],
  isAdvisor: boolean,
): Pick<AdvicePriceFormValues, DisabilityOneTimePaymentFormValues> => {
  const isOneTimePaymentChosenByCustomer =
    getProductOneTimePaymentCheckedValue(changes);
  const hasAdvisorChangedOneTimePayment = isDefined(
    getInsurance(changes, INSURANCE_NAME_VALUE.DISABILITY_FLEX)?.additionalData
      ?.disabilityOneTimePaymentChoice,
  );
  if (
    isAdvisor &&
    isOneTimePaymentChosenByCustomer &&
    !hasAdvisorChangedOneTimePayment
  ) {
    const oneTimePaymentForCustomer = getProductOneTimePaymentValue(changes);
    return {
      disabilityOneTimePayment: getProductOneTimePaymentCheckedValue(changes),
      disabilityOneTimePaymentValue: oneTimePaymentForCustomer ?? 0,
      disabilityOneTimePaymentAdvisorChoice:
        OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT,
      disabilityOneTimePaymentAdvisorValue: formatNumber(
        String(oneTimePaymentForCustomer ?? 0),
        false,
      ),
    };
  }
  return {
    disabilityOneTimePayment: getProductOneTimePaymentCheckedValue(changes),
    disabilityOneTimePaymentValue: getProductOneTimePaymentValue(changes) ?? 0,
    disabilityOneTimePaymentAdvisorChoice: isAdvisor
      ? getProductOneTimePaymentAdvisorChoice(changes)
      : undefined,
    disabilityOneTimePaymentAdvisorValue: isAdvisor
      ? getProductOneTimePaymentAdvisorValue(changes)
      : undefined,
  };
};
